<template>
  <section v-if="hasData" class="sa-rep-charts" aria-label="Gráficos do relatório">
    <DashboardChartCard
      :title="primaryTitle"
      :subtitle="primarySubtitle"
      emphasis
    >
      <VueApexCharts
        :key="`${tab}-primary`"
        :type="primaryType"
        height="280"
        width="100%"
        :options="primaryOptions"
        :series="primarySeries"
      />
    </DashboardChartCard>

    <DashboardChartCard
      :title="secondaryTitle"
      :subtitle="secondarySubtitle"
    >
      <VueApexCharts
        :key="`${tab}-secondary`"
        :type="secondaryType"
        height="280"
        width="100%"
        :options="secondaryOptions"
        :series="secondarySeries"
      />
    </DashboardChartCard>
  </section>
</template>

<script>
import DashboardChartCard from '@/components/Weflix/SuperAdmin/Dashboard/DashboardChartCard.vue';
import {
  formatCompactCurrency,
  formatCurrencyBRL,
  formatNumberBR,
} from '@/utils/superAdmin/formatters';

const COLORS = {
  primary: '#250891',
  secondary: '#1AC7D0',
  accent: '#7C5CFC',
  muted: '#94A3B8',
};

function shortName(name, max = 18) {
  const text = String(name || '—').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function topRows(rows, key, limit = 8) {
  const sorted = [...rows]
    .sort((a, b) => Number(b?.[key] || 0) - Number(a?.[key] || 0));
  const withValue = sorted
    .filter((row) => Number(row?.[key] || 0) > 0)
    .slice(0, limit);
  if (withValue.length) return withValue;
  return sorted.slice(0, Math.min(limit, sorted.length));
}

function baseChart() {
  return {
    chart: {
      toolbar: { show: false },
      fontFamily: 'Inter, system-ui, sans-serif',
      animations: { enabled: true, speed: 400 },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    grid: {
      borderColor: '#edf0f5',
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      padding: { left: 8, right: 8 },
    },
    legend: {
      position: 'bottom',
      fontSize: '12px',
      labels: { colors: '#697386' },
    },
  };
}

export default {
  name: 'ReportsCharts',
  components: { DashboardChartCard },
  props: {
    tab: { type: String, default: 'clients' },
    rows: { type: Array, default: () => [] },
  },
  computed: {
    hasData() {
      return Array.isArray(this.rows) && this.rows.length > 0;
    },
    primaryTitle() {
      if (this.tab === 'financial') return 'Receita e líquido por empresa';
      if (this.tab === 'coursesSales') return 'Top empresas por vendas';
      return 'Top empresas por faturamento';
    },
    primarySubtitle() {
      if (this.tab === 'financial') return 'Comparativo das maiores receitas no filtro atual';
      if (this.tab === 'coursesSales') return 'Ranking das maiores volumes de venda';
      return 'Ranking das maiores faturamentos no filtro atual';
    },
    secondaryTitle() {
      if (this.tab === 'financial') return 'Composição financeira';
      if (this.tab === 'coursesSales') return 'Cursos vs vendas';
      return 'Distribuição da base';
    },
    secondarySubtitle() {
      if (this.tab === 'financial') return 'Receita, taxas e líquido consolidados';
      if (this.tab === 'coursesSales') return 'Top empresas por quantidade de cursos';
      return 'Empresas por sistema e tipo';
    },
    primaryType() {
      return 'bar';
    },
    secondaryType() {
      if (this.tab === 'coursesSales') return 'bar';
      return 'donut';
    },
    clientsTop() {
      return topRows(this.rows, 'revenue');
    },
    financialTop() {
      return topRows(this.rows, 'revenue');
    },
    salesTop() {
      return topRows(this.rows, 'sales');
    },
    coursesTop() {
      return topRows(this.rows, 'courses');
    },
    distribution() {
      let weeducEscolar = 0;
      let weeducCorp = 0;
      let weflix = 0;
      let weeducOther = 0;

      this.rows.forEach((row) => {
        if (Number(row.typeSystem) === 2) {
          weflix += 1;
          return;
        }
        if (Number(row.typeBusiness) === 1) weeducEscolar += 1;
        else if (Number(row.typeBusiness) === 2) weeducCorp += 1;
        else weeducOther += 1;
      });

      const items = [
        { label: 'Weeduc · Escolar', value: weeducEscolar, color: COLORS.primary },
        { label: 'Weeduc · Corporativo', value: weeducCorp, color: COLORS.accent },
        { label: 'Weeduc · Sem tipo', value: weeducOther, color: COLORS.muted },
        { label: 'Weflix', value: weflix, color: COLORS.secondary },
      ].filter((item) => item.value > 0);

      return items;
    },
    financialTotals() {
      return this.rows.reduce(
        (acc, row) => {
          acc.revenue += Number(row.revenue || 0);
          acc.fees += Number(row.fees || 0);
          acc.net += Number(row.net || 0);
          return acc;
        },
        { revenue: 0, fees: 0, net: 0 },
      );
    },
    primarySeries() {
      if (this.tab === 'financial') {
        return [
          {
            name: 'Receita',
            data: this.financialTop.map((row) => Number(row.revenue || 0)),
          },
          {
            name: 'Líquido',
            data: this.financialTop.map((row) => Number(row.net || 0)),
          },
        ];
      }
      if (this.tab === 'coursesSales') {
        return [{
          name: 'Vendas',
          data: this.salesTop.map((row) => Number(row.sales || 0)),
        }];
      }
      return [{
        name: 'Faturamento',
        data: this.clientsTop.map((row) => Number(row.revenue || 0)),
      }];
    },
    primaryOptions() {
      const categories = this.tab === 'financial'
        ? this.financialTop.map((row) => shortName(row.name))
        : this.tab === 'coursesSales'
          ? this.salesTop.map((row) => shortName(row.name))
          : this.clientsTop.map((row) => shortName(row.name));

      const isMoney = this.tab !== 'coursesSales';

      return {
        ...baseChart(),
        chart: {
          ...baseChart().chart,
          type: 'bar',
        },
        colors: this.tab === 'financial'
          ? [COLORS.primary, COLORS.secondary]
          : [COLORS.primary],
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 5,
            borderRadiusApplication: 'end',
            barHeight: this.tab === 'financial' ? '68%' : '55%',
          },
        },
        xaxis: {
          categories,
          labels: {
            style: { colors: '#697386', fontSize: '11px' },
            formatter: (val) => (isMoney ? formatCompactCurrency(val) : formatNumberBR(val)),
          },
        },
        yaxis: {
          labels: {
            style: { colors: '#697386', fontSize: '11px' },
            maxWidth: 120,
          },
        },
        tooltip: {
          y: {
            formatter: (val) => (isMoney ? formatCurrencyBRL(val) : formatNumberBR(val)),
          },
        },
        legend: {
          show: this.tab === 'financial',
          position: 'top',
          horizontalAlign: 'left',
          fontSize: '12px',
          labels: { colors: '#697386' },
        },
      };
    },
    secondarySeries() {
      if (this.tab === 'financial') {
        const { revenue, fees, net } = this.financialTotals;
        return [revenue, fees, net].map((value) => Math.max(0, Number(value) || 0));
      }
      if (this.tab === 'coursesSales') {
        return [{
          name: 'Cursos',
          data: this.coursesTop.map((row) => Number(row.courses || 0)),
        }];
      }
      return this.distribution.map((item) => item.value);
    },
    secondaryOptions() {
      if (this.tab === 'coursesSales') {
        return {
          ...baseChart(),
          chart: {
            ...baseChart().chart,
            type: 'bar',
          },
          colors: [COLORS.secondary],
          plotOptions: {
            bar: {
              borderRadius: 6,
              borderRadiusApplication: 'end',
              columnWidth: '48%',
            },
          },
          xaxis: {
            categories: this.coursesTop.map((row) => shortName(row.name, 14)),
            labels: {
              rotate: -25,
              style: { colors: '#697386', fontSize: '11px' },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: {
            labels: {
              style: { colors: '#697386', fontSize: '11px' },
              formatter: (val) => formatNumberBR(val),
            },
          },
          tooltip: {
            y: { formatter: (val) => formatNumberBR(val) },
          },
          legend: { show: false },
        };
      }

      const labels = this.tab === 'financial'
        ? ['Receita', 'Taxas', 'Líquido']
        : this.distribution.map((item) => item.label);
      const colors = this.tab === 'financial'
        ? [COLORS.primary, COLORS.accent, COLORS.secondary]
        : this.distribution.map((item) => item.color);
      const total = this.secondarySeries.reduce((sum, value) => sum + Number(value || 0), 0);

      return {
        ...baseChart(),
        chart: {
          ...baseChart().chart,
          type: 'donut',
        },
        labels,
        colors,
        legend: {
          position: 'bottom',
          fontSize: '12px',
          labels: { colors: '#697386' },
        },
        stroke: { width: 2, colors: ['#fff'] },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#697386',
                  offsetY: 16,
                },
                value: {
                  show: true,
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#191c24',
                  offsetY: -8,
                  formatter: (val) => (
                    this.tab === 'financial'
                      ? formatCompactCurrency(val)
                      : formatNumberBR(val)
                  ),
                },
                total: {
                  show: true,
                  label: this.tab === 'financial' ? 'Receita' : 'Empresas',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#697386',
                  formatter: () => (
                    this.tab === 'financial'
                      ? formatCompactCurrency(this.financialTotals.revenue)
                      : formatNumberBR(total)
                  ),
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val) => (
              this.tab === 'financial'
                ? formatCurrencyBRL(val)
                : `${formatNumberBR(val)} empresas`
            ),
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.sa-rep-charts {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 0.9rem;
  min-width: 0;
}

.sa-rep-charts :deep(.sa-chart-card) {
  background: #fafbfe;
}

@media (max-width: 960px) {
  .sa-rep-charts {
    grid-template-columns: 1fr;
  }
}
</style>
